import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDateShort, isToday } from '../utils'

interface DateNavigatorProps {
  selectedDate: Date
  onDateChange: (date: Date) => void
  onPreviousDay: () => void
  onNextDay: () => void
  onToday: () => void
  className?: string
}

export function DateNavigator({
  selectedDate,
  onDateChange,
  onPreviousDay,
  onNextDay,
  onToday,
  className,
}: DateNavigatorProps) {
  const { t } = useTranslation('orders')
  const isTodaySelected = isToday(selectedDate)

  return (
    <div className={cn('flex items-center gap-1.5 sm:gap-2 min-w-0', className)}>
      {/* Segmented prev / date / next control */}
      <div className="inline-flex items-stretch rounded-lg border border-border/80 bg-card shadow-sm shadow-black/[0.03] overflow-hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={onPreviousDay}
          title={t('dateNav.prevDay')}
          aria-label={t('dateNav.prevDay')}
          className="h-9 w-9 rounded-none text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'h-9 rounded-none border-x border-border/60 px-3 min-w-[112px] sm:min-w-[136px] justify-center font-medium',
                !isTodaySelected && 'text-primary'
              )}
            >
              <CalendarDays className="me-2 h-4 w-4 text-muted-foreground" />
              {formatDateShort(selectedDate)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && onDateChange(date)}
              autoFocus
            />
          </PopoverContent>
        </Popover>

        <Button
          variant="ghost"
          size="icon"
          onClick={onNextDay}
          title={t('dateNav.nextDay')}
          aria-label={t('dateNav.nextDay')}
          className="h-9 w-9 rounded-none text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4 rtl:rotate-180" />
        </Button>
      </div>

      {!isTodaySelected && (
        <Button
          variant="outline"
          size="sm"
          onClick={onToday}
          className="h-9 hidden sm:inline-flex"
        >
          {t('dateNav.today')}
        </Button>
      )}
    </div>
  )
}
